import { useAlert } from 'react-alert';
import { useForm } from 'react-hook-form';
import { generatePath } from 'react-router-dom';
import I18n from 'i18n-js';

import { redeemCode } from '@lootboy/lib/actions/CodeActions';

import Alert from '~/components/Alert';
import Input from '~/components/atoms/FormControls/Input';
import StyledButton, {
  EStyledButtonVariant,
} from '~/components/atoms/StyledButton';
import GoogleTagManager from '~/components/GoogleTagManager';
import Markdown from '~/components/Markdown';
import { MenuViewHeader } from '~/components/MenuViewHeader';
import { AuthViewHeadline } from '~/components/molecules/AuthViewHeadline';

import { useClosePopup } from '~/hooks/useClosePopup';
import { useURLSearchParams } from '~/hooks/useURLSearchParams';
import { executeRecaptcha } from '~/middlewares/recaptcha';
import { emitSound, MONEY_SHOT } from '~/redux/actions/SoundActions';
import { useAppDispatch } from '~/redux/store/useAppDispatch';
import { OPEN_CARDS_PATH, OPEN_PURCHASE_PATH } from '~/routes/paths';
import { parseLootBoyError } from '~/utilities/errors';
import { useAppHistory } from '~/utilities/routing';

import styles from './RedeemCode.module.scss';

type TRedeemCodeFormValues = {
  code: string;
};

const REDEEM_FORM_ID = 'redeem_code_form';

export function RedeemCodePopup(): JSX.Element {
  const alert = useAlert();
  const dispatch = useAppDispatch();
  const closePopup = useClosePopup();
  const code = useURLSearchParams('code');
  const history = useAppHistory();

  const {
    register,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm<TRedeemCodeFormValues>({ defaultValues: { code: code ?? '' } });

  const onSubmit = async (values: TRedeemCodeFormValues) => {
    try {
      const token: string = await executeRecaptcha('code_redeem');
      const redeemResponse = await dispatch(
        redeemCode(values.code, token, { 'x-recaptcha-enterprise': true }),
      );
      if (!redeemResponse?.gotBonus) {
        alert.info(
          <Alert
            body={I18n.t('RedeemCode_already_redeemed')}
            title={I18n.t('RedeemCode_already_redeemed_title')}
          />,
        );
        return;
      }
      GoogleTagManager.dataLayer({
        event: 'code_successfully_redeemed',
        code: values.code.trim(),
      });
      if (redeemResponse.lootcoinBonus || redeemResponse.lootgemBonus) {
        dispatch(emitSound(MONEY_SHOT));
      }
      switch (redeemResponse.type) {
        case 'lootgem':
        case 'lootcoin': {
          alert.success(
            <Alert
              body={I18n.t('common_redeemed_successfully')}
              title={I18n.t('common_success')}
            />,
          );
          closePopup();
          break;
        }
        case 'lootpack': {
          if (redeemResponse.purchase?.id) {
            history.replace(
              generatePath(OPEN_PURCHASE_PATH, {
                purchaseId: redeemResponse.purchase?.id,
              }),
            );
          }
          break;
        }
        case 'lootcard': {
          if (redeemResponse.card) {
            history.replace(OPEN_CARDS_PATH, { cards: [redeemResponse.card] });
          }
          break;
        }
        default: {
          closePopup();
        }
      }
    } catch (error) {
      const { message } = parseLootBoyError(error);
      alert.error(
        <Alert
          body={message}
          title={I18n.t('RedeemCode_failed_title')}
        />,
      );
    }
  };

  return (
    <>
      <MenuViewHeader title={I18n.t('MenuView_redeem_bc')} />
      <section className={styles.container}>
        <AuthViewHeadline
          headline={I18n.t('MenuView_redeem_headline')}
          subHeadline={I18n.t('MenuView_redeem_sub')}
        />
        <form
          className={styles.form}
          onSubmit={handleSubmit(onSubmit)}
          id={REDEEM_FORM_ID}
        >
          <Input
            {...register('code', { required: true })}
            label={I18n.t('MenuView_redeem_code')}
            id="code"
            placeholder={I18n.t('MenuView_redeem_placeholder')}
          />
        </form>
      </section>
      <footer className={styles.footer}>
        <Markdown className={styles.captchaNotice}>
          {I18n.t('LoginView_recaptcha_usage_info')}
        </Markdown>
        <StyledButton
          variant={EStyledButtonVariant.BLUE}
          className={styles.footerButton}
          disabled={!isValid || isSubmitting}
          type="submit"
          form={REDEEM_FORM_ID}
        >
          {I18n.t('common_redeem')}
        </StyledButton>
      </footer>
    </>
  );
}
